import { pxToDP } from '@capsicum/common';
import { BaseTextInput, GenericDrawer } from '@capsicum/common-web';
import { WarningIcon } from '@capsicum/common-web/icons';
import { type BidAmountProps, useBidAmount } from '@capsicum/features/fanshop/shared';
import { type ChangeEvent } from 'react';
import styled, { useTheme } from 'styled-components';

export const BidAmount = ({
	chzCurrencyShortCode,
	chzRate,
	closeModal,
	country,
	initialBidAmount,
	mainCurrencyShortcode,
	mainCurrencySymbol,
	minimumBid,
	onPress,
	shippingCost,
	testID,
	userAmount
}: BidAmountProps) => {
	const theme = useTheme();
	const { amount, getChzAmount, getChzShippingCost, onButtonPress, setAmount, texts, validateAmount } = useBidAmount({
		chzRate,
		closeModal,
		country,
		initialBidAmount,
		mainCurrencyShortcode,
		minimumBid,
		onPress,
		shippingCost,
		userAmount
	});

	const Warning = () => (
		<S.StyledWarning>
			<S.StyledWaringIcon />
			<S.WarningText>{texts.warning}</S.WarningText>
		</S.StyledWarning>
	);

	const WarningEstimation = () => <S.WarningText>{texts.warningEstimation}</S.WarningText>;

	const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		const text = e.target.value;
		const regex = /^[0-9\b]+$/;

		if (text === '' || regex.test(text)) {
			setAmount(Number(text));
		}
	};

	return (
		<GenericDrawer
			buttonDisabled={validateAmount(amount, shippingCost, minimumBid, userAmount) !== undefined}
			buttonText={texts.buttonText}
			elementStyles={{ marginTop: pxToDP(theme.spacings.xxxxsmall) }}
			testID={testID}
			title={texts.title}
			onButtonPress={onButtonPress}
		>
			<Warning />

			<BaseTextInput
				componentEnd={<S.MainCurrencyShortcode>{mainCurrencyShortcode}</S.MainCurrencyShortcode>}
				error={validateAmount(amount, shippingCost, minimumBid, userAmount)}
				supportingTextRight={`≈ ${getChzAmount()} ${chzCurrencyShortCode}`}
				testID="auctionPageDrawer-bidAmount-text"
				value={amount.toString()}
				onChange={onInputChange}
			/>

			<S.StyledText>
				<S.Price>{`${shippingCost}${mainCurrencySymbol}`}</S.Price>
				<S.PriceChz>{` (${getChzShippingCost()} ${chzCurrencyShortCode}) `}</S.PriceChz>
				{texts.shippingSummary}
			</S.StyledText>

			<WarningEstimation />
		</GenericDrawer>
	);
};

namespace S {
	export const StyledWarning = styled.div<{ $smallMargin?: boolean }>`
		display: flex;
		flex-direction: row;
		background-color: ${({ theme }) => theme.colors.backgrounds.warning.subtle};
		padding: ${({ theme }) => theme.spacings.xxxsmall};
		margin-bottom: ${({ $smallMargin, theme }) => ($smallMargin ? theme.spacings.xxsmall : theme.spacings.small)};
		${({ theme }) => theme.border.radius.medium};
		align-items: center;
	`;

	export const StyledWaringIcon = styled(WarningIcon)`
		margin-right: ${({ theme }) => theme.spacings.xxsmall};
		color: ${({ theme }) => theme.colors.icons.main.enabled};
		flex-shrink: 0;
	`;

	export const WarningText = styled.div`
		${({ theme }) => theme.fonts.text.text5};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		flex-shrink: 1;
	`;

	export const MainCurrencyShortcode = styled.span`
		${({ theme }) => theme.fonts.text.text4};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		align-self: center;
	`;

	export const StyledText = styled.div`
		${({ theme }) => theme.fonts.text.caption2};
		margin-top: ${({ theme }) => theme.spacings.medium};
		margin-bottom: ${({ theme }) => theme.spacings.medium};
		padding: ${({ theme }) => theme.spacings.xxsmall};

		${({ theme }) => theme.border.radius.medium};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		border: solid;
		border-width: ${({ theme }) => theme.border.width.small};
		border-color: ${({ theme }) => theme.colors.borders.main.divider};
	`;

	export const Price = styled.span`
		${({ theme }) => theme.fonts.text.caption1};
	`;

	export const PriceChz = styled.span`
		${({ theme }) => theme.fonts.text.caption1};
		color: ${({ theme }) => theme.colors.text.main.subtle};
	`;
}
